import classNames from 'classnames';
import { Icon, IconsEnum } from 'components/Icons';
import { selectIsOpenCarrello } from 'features/carrello/carrelloSelectors';
import { useMenu } from 'features/live/liveNav/hooks';
import { selectIsLiveNavLoading } from 'features/live/liveNav/selectors';
import useWindowSize from 'hooks/useWindowSize';
import { useTypedSelector } from 'lib/centralStore';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { isServerSide } from 'utility/constant';
import { SportTopNavigationItem } from './compontents/SportTopNavigationItem';
import styles from './SportTopNavigation.module.scss';

export type SportTopNavigationProps = {
  isLive?: boolean;
  noPadding?: boolean;
  hasFirstElementFixed?: boolean;
  isOnlyDesktop?: boolean;
};

export const SportTopNavigation = memo(function SportTopNavigation({
  isLive,
  noPadding,
  hasFirstElementFixed,
  isOnlyDesktop = false,
}: SportTopNavigationProps) {
  const toScroll = useRef(true);
  const isOpen = useTypedSelector(selectIsOpenCarrello);
  const isLoading = useTypedSelector(selectIsLiveNavLoading);

  const containerRef = useRef<HTMLUListElement>(null);
  const { isMobile } = useWindowSize();

  const { ts, discipline } = useMenu({ isTop: true, isLive });

  const [isAtEnd, setIsAtEnd] = useState(false);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isScrollable, setIsScrollable] = useState(false);

  const onScrollHandler = useCallback(() => {
    if (containerRef.current) {
      const currentPosition = containerRef.current.scrollLeft;
      const isStart = currentPosition === 0;
      const isEnd = currentPosition === containerRef.current.scrollWidth - containerRef.current.clientWidth;

      setIsAtStart(isStart);
      setIsAtEnd(isEnd);
    }
  }, []);

  const onResizeHandler = useCallback(() => {
    if (!containerRef.current) return;

    toScroll.current = false;
    const nextIsScrollable = containerRef.current.scrollWidth > containerRef.current.clientWidth;
    if (isScrollable !== nextIsScrollable) {
      setIsScrollable(nextIsScrollable);
    }

    onScrollHandler();
  }, [isScrollable, onScrollHandler]);

  useEffect(() => {
    if (isServerSide) return;

    if (isLoading) {
      toScroll.current = true;
    } else if (toScroll.current && containerRef.current) {
      onResizeHandler();
    }
  }, [isLoading, onResizeHandler]);

  const goBack = (refElement: HTMLUListElement | null) => {
    const delta = refElement?.offsetWidth;

    if (refElement && delta) {
      refElement.scrollLeft -= delta - 250;
    }
  };

  const goOn = (refElement: HTMLUListElement | null) => {
    const delta = refElement?.offsetWidth;

    if (refElement && delta) {
      refElement.scrollLeft += delta - 250;
    }
  };

  useEffect(() => {
    if (isServerSide) return;

    onResizeHandler();
    window.addEventListener('resize', onResizeHandler, { passive: true });

    return () => {
      if (isServerSide) return;

      window.removeEventListener('resize', onResizeHandler);
    };
  }, [onResizeHandler]);

  return (
    <nav className={classNames(styles.container, { [styles.mobileHidden]: isOnlyDesktop })} suppressHydrationWarning>
      <div className={classNames(styles.wrapper, { [styles.wrapperLive]: isLive })}>
        {isScrollable && !isAtStart && (
          <button
            className={classNames(
              styles.button,
              styles.buttonLeft,
              { [styles.leftFixed]: hasFirstElementFixed },
              { [styles.hidden]: isMobile }
            )}
            suppressHydrationWarning
            onClick={() => goBack(containerRef.current)}
          >
            <Icon iconId={IconsEnum.CHEVRON_LEFT} className={styles.icon} color="var(--color-on-bg-primary)" />
          </button>
        )}

        <ul
          ref={containerRef}
          className={classNames(styles.list, { [styles.noPadding]: noPadding || isOpen })}
          onScroll={onScrollHandler}
        >
          {discipline.map(({ id }, index: number) => {
            const isFixed = hasFirstElementFixed ? index === 0 : false;
            return (
              <SportTopNavigationItem
                key={`top-menu-${id}-${ts}`}
                isLive={isLive}
                isFixed={isFixed}
                idDisciplina={`${id}`}
              />
            );
          })}
        </ul>

        {isScrollable && !isAtEnd && (
          <button
            className={classNames(styles.button, styles.buttonRight, { [styles.hidden]: isMobile })}
            suppressHydrationWarning
            onClick={() => goOn(containerRef.current)}
          >
            <Icon iconId={IconsEnum.CHEVRON_RIGHT} className={styles.icon} color="var(--color-on-bg-primary)" />
          </button>
        )}
      </div>
    </nav>
  );
});
